import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { ExternalLink, LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { editorials, findEditorial } from '../../config/editorials';
import EditorialCard from '../LandingPage/sections/SectionEditorial/EditorialCard/EditorialCard';

import marketingCss from '../_marketingPage/marketingPage.module.css';
import css from './MagasinArticlePage.module.css';

/**
 * Render a single paragraph block. Supports either a plain `text`
 * string or a `segments` array of `{ text, bold?, italic?, href? }` runs for
 * inline emphasis and links without HTML in the data.
 */
const Paragraph = ({ block }) => {
  if (block.segments) {
    return (
      <p className={css.body}>
        {block.segments.map((seg, i) => {
          let node = seg.text;
          if (seg.bold) node = <strong key={`b-${i}`}>{node}</strong>;
          if (seg.italic) node = <em key={`i-${i}`}>{node}</em>;
          // An internal path stays in the app; the copy links to our own
          // pages, so NamedLink is no use here — the target is data.
          if (seg.href) {
            node = (
              <Link key={`a-${i}`} className={css.inlineLink} to={seg.href}>
                {node}
              </Link>
            );
          }
          return <React.Fragment key={i}>{node}</React.Fragment>;
        })}
      </p>
    );
  }
  return <p className={css.body}>{block.text}</p>;
};

/**
 * List items can be either a plain string or an `{ lead, text }` pair
 * where `lead` is rendered bold at the start of the bullet — mirrors
 * the "Bold lead — rest of sentence" pattern in the source PDFs.
 */
const Item = ({ item }) => {
  if (typeof item === 'string') return <>{item}</>;
  return (
    <>
      <strong>{item.lead}</strong> {item.text}
    </>
  );
};

const renderBlock = (block, i) => {
  switch (block.type) {
    case 'heading': {
      const level = block.level === 3 ? 3 : 2;
      const className = level === 3 ? css.subheading : css.heading;
      const Tag = level === 3 ? 'h3' : 'h2';
      return (
        <Tag key={i} className={className}>
          {block.text}
        </Tag>
      );
    }
    case 'paragraph':
      return <Paragraph key={i} block={block} />;
    case 'list':
      return (
        <ul key={i} className={css.list}>
          {block.items.map((item, j) => (
            <li key={j} className={css.listItem}>
              <Item item={item} />
            </li>
          ))}
        </ul>
      );
    case 'ordered-list':
      return (
        <ol key={i} className={css.orderedList}>
          {block.items.map((item, j) => (
            <li key={j} className={css.orderedItem}>
              <Item item={item} />
            </li>
          ))}
        </ol>
      );
    case 'image': {
      // One photo, or a pair shown side by side the way Guro laid the detail
      // shots out in her document. A pair stacks on a phone rather than
      // shrinking to two unreadable thumbnails.
      const images = block.images || [];
      if (images.length === 0) {
        return null;
      }
      return (
        <figure key={i} className={images.length > 1 ? css.figurePair : css.figure}>
          {images.map((image, j) => (
            <img
              key={j}
              className={css.figureImage}
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading="lazy"
              decoding="async"
            />
          ))}
        </figure>
      );
    }
    case 'pullout':
      return (
        <p key={i} className={css.pullout}>
          {block.text}
        </p>
      );
    case 'cta':
      // External-looking but always internal — `/l/new` is the
      // NewListingPage. Use a regular link so the href surfaces in
      // SSR / share previews; the SPA router still handles it.
      return (
        <div key={i} className={css.ctaWrap}>
          <ExternalLink href={block.href} className={css.cta}>
            {block.label}
          </ExternalLink>
        </div>
      );
    default:
      return null;
  }
};

/**
 * Magasin article detail page (`/magasin/:slug`).
 *
 * Looks up the editorial from `src/config/editorials.js` by the `:slug`
 * route param. Renders the marketing-page hero (eyebrow + title + lede +
 * hero image) followed by the block list, then a "related articles"
 * strip with every other editorial.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @param {Object} props.params - React-Router params; we read `slug`
 * @returns {JSX.Element}
 */
const MagasinArticlePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const { scrollingDisabled, params, staticContext } = props;
  const slug = params?.slug;
  const editorial = findEditorial(slug);

  if (!editorial) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const related = editorials.filter(e => e.slug !== editorial.slug);

  // Structured data for the article. The client asked for the publication
  // date to be published for SEO; the visible date above is what a reader
  // sees, and `datePublished` here is what a search engine reads. Absolute
  // URLs, because a crawler has no page context to resolve a path against.
  const rootURL = (config.marketplaceRootURL || '').replace(/\/$/, '');
  const articleSchema = {
    '@context': 'http://schema.org',
    '@type': 'Article',
    headline: editorial.title,
    description: editorial.lede,
    datePublished: editorial.publishedAt,
    ...(editorial.image ? { image: [`${rootURL}${editorial.image}`] } : {}),
    mainEntityOfPage: `${rootURL}/magasin/${editorial.slug}`,
    author: { '@type': 'Organization', name: config.marketplaceName },
    publisher: { '@type': 'Organization', name: config.marketplaceName },
  };
  const formattedDate = editorial.publishedAt
    ? intl.formatDate(new Date(editorial.publishedAt), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <Page
      title={intl.formatMessage(
        { id: 'MagasinArticlePage.metaTitle' },
        { title: editorial.title }
      )}
      description={editorial.lede}
      scrollingDisabled={scrollingDisabled}
      schema={articleSchema}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <article className={marketingCss.root}>
          <div className={marketingCss.content}>
            <header className={marketingCss.hero}>
              {editorial.eyebrow ? (
                <p className={marketingCss.heroEyebrow}>{editorial.eyebrow}</p>
              ) : null}
              <h1 className={marketingCss.heroHeading}>{editorial.title}</h1>
              {editorial.lede ? (
                <p className={marketingCss.lede}>{editorial.lede}</p>
              ) : null}
              {formattedDate || editorial.readingMinutes ? (
                <p className={css.meta}>
                  {formattedDate}
                  {formattedDate && editorial.readingMinutes ? ' · ' : null}
                  {editorial.readingMinutes
                    ? intl.formatMessage(
                        { id: 'MagasinArticlePage.readingTime' },
                        { minutes: editorial.readingMinutes }
                      )
                    : null}
                </p>
              ) : null}
            </header>

            {editorial.image ? (
              <figure className={css.heroFigure}>
                <img
                  className={css.heroImage}
                  src={editorial.image}
                  alt={editorial.imageAlt || ''}
                />
              </figure>
            ) : null}

            <div className={css.prose}>{editorial.blocks.map(renderBlock)}</div>

            <NamedLink className={css.backLink} name="MagasinPage">
              {intl.formatMessage({ id: 'MagasinArticlePage.backLink' })}
            </NamedLink>

            {related.length > 0 ? (
              <section className={css.related}>
                <h2 className={css.relatedHeading}>
                  {intl.formatMessage({ id: 'MagasinArticlePage.relatedHeading' })}
                </h2>
                <div className={css.relatedGrid}>
                  {related.map(e => (
                    <EditorialCard key={e.slug} editorial={e} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const MagasinArticlePage = connect(mapStateToProps)(MagasinArticlePageComponent);

export { MagasinArticlePageComponent };
export default MagasinArticlePage;
